import {
  EMediaSequenceState,
  EPermission,
  MediaSequenceResponse,
} from 'api/core';
import {
  useGetIsValidFormat,
  useGetMediaSequence,
  useInvokeMediaSequence,
  useRedoMediaSequence,
} from 'api/useMediaSequencesApi';
import { useAuth } from 'auth/AuthProvider';
import { MutateEntity } from 'components/Form/MutateEntity';
import { useCallback, useEffect, useState } from 'react';
import { copyCurrentLocationLinkToClipboard } from 'utils/clipboard';
import { useMediaSequenceSetup } from './MediaSequenceSetupProvider';
import { PermissionProtectedComponent } from 'auth/PermissionProtectedComponent';
import { MediaSequenceProgress } from './MediaSequenceProgress';
import { MediaSequenceBranchResultTable } from 'pages/MediaOrbit/components/MediaSequenceBranch/MediaSequenceBranchResultTable';
import { isTemplate } from 'pages/MediaOrbit/media-sequence-utils';
import { MediaSequenceCreate } from 'pages/MediaOrbit/components/MediaSequence/MediaSequenceCreate';

interface MediaSequenceSetupResultProps {
  mediaSequence: MediaSequenceResponse;
  disabled: boolean;
}

export const MediaSequenceSetupResult = ({
  mediaSequence,
  disabled,
}: MediaSequenceSetupResultProps) => {
  const { handlePrevious, handleNext } = useMediaSequenceSetup();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [disableNext, setDisableNext] = useState<boolean>(false);
  const [disableText, setDisableText] = useState<string>('');

  const { data } = useGetMediaSequence(mediaSequence.id, {
    refetchInterval: () => {
      if (mediaSequence.state === EMediaSequenceState.Processing) {
        return 5000;
      }

      if (
        mediaSequence.state === EMediaSequenceState.Processed &&
        mediaSequence.branches.some((branch) => !branch.asset)
      ) {
        return 5000;
      }

      return undefined;
    },
  });

  const { data: isValid } = useGetIsValidFormat(mediaSequence.id);

  const getEmailLink = () => {
    if (!user || !data) return '';
    const subject = `Smart Video færdig: ${data.name}`;
    const newLine = '%0D%0A';

    const baseLinkUrl = `${process.env.REACT_APP_CORE_API_URL}/download`;
    const branchLinks = data.branches.map((branch) => {
      const branchUrl = `${baseLinkUrl}/${branch.asset?.url.split('/').pop()}`;
      return `${branch.name}: ${branchUrl}`;
    });

    const body = `Hej${newLine}${newLine}Smart Videoen til ${data.name} er nu færdig, de forskellige formater kan downloades her:${newLine}${newLine}${branchLinks.join(
      newLine
    )}${newLine}${newLine}Mvh${newLine}${user.name}`;
    return `mailto:?subject=${subject}&body=${body}`;
  };

  const getSmsLink = () => {
    if (!user || !data) return '';
    const newLine = '%0A'; // Newline for SMS

    const baseLinkUrl = `${process.env.REACT_APP_CORE_API_URL}/download`;
    const branchLink = `${baseLinkUrl}/${data.branches[0].asset?.url.split('/').pop()}`;

    const body = `Hej,${newLine}${newLine}Videoen ${data.name} kan ses her:${newLine}${branchLink}${newLine}${newLine}Mvh ${user.name}`;

    // Create SMS link (note: the `sms:` URL scheme doesn't allow subject, only body)
    return `sms:?body=${body}`;
  };

  const onRevalidateStep = useCallback(() => {
    if (mediaSequence.state !== EMediaSequenceState.Processed) {
      setDisableNext(true);
      setDisableText('Der er ikke flere trin');
    } else {
      setDisableNext(true);
      setDisableText('Der er ikke flere trin');
    }
  }, [mediaSequence.state]);

  useEffect(() => {
    onRevalidateStep();
  }, [mediaSequence, onRevalidateStep]);

  if (!data) return null;

  return (
    <>
      {!isValid ? (
        <div className="alert alert-error mb-4 flex justify-center rounded-none text-white">
          <span>
            Denne video er oprettet med et ældre format, som ikke længere
            understøttes. Det betyder, at du desværre ikke kan lave den om eller
            tage en kopi af den. Du har dog stadig mulighed for at se og
            downloade videoen.
          </span>
        </div>
      ) : null}
      <div className="space-y-4">
        {data.state === EMediaSequenceState.Draft ? (
          <div className="space-y-4">
            {!isTemplate(data) ? (
              <p>
                Når du trykker på knappen nedenfor vil Smart Video starte med at
                lave sammenklippe din video. Det betyder at du ikke kan ændre i
                videoen efterfølgende, så sørg for at alt er som det skal være
                før du trykker på knappen.
              </p>
            ) : (
              <p>Dette er en skabelon. Du kan ikke færdiggøre denne video</p>
            )}
            <MutateEntity
              trigger={
                <button
                  id="results-finalize"
                  className="btn btn-primary"
                  disabled={isLoading || isTemplate(data)}
                >
                  Færdiggør video
                </button>
              }
              title={`Start ${data.name} video?`}
              description={`Er du sikker på du vil færdiggøre videoen '${mediaSequence.name}'? Du kan ikke ændre i den efterfølgende.`}
              setter={useInvokeMediaSequence}
              mutationVariables={{ id: data.id }}
              onMutate={() => setIsLoading(false)}
              onMutateConfirm={() => setIsLoading(true)}
              hidden={disabled}
            />
          </div>
        ) : null}
        {mediaSequence.state === EMediaSequenceState.Processing ? (
          <MediaSequenceProgress mediaSequence={mediaSequence} />
        ) : null}
        {mediaSequence.state === EMediaSequenceState.Processed ? (
          <>
            <div className="flex justify-start w-full space-x-2">
              <div className="dropdown dropdown-hover">
                <div
                  data-testid="media-sequence-share"
                  tabIndex={0}
                  role="button"
                  className="btn btn-primary"
                >
                  Del resultat
                </div>
                <ul
                  tabIndex={0}
                  className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52"
                >
                  <li>
                    <a onClick={copyCurrentLocationLinkToClipboard}>
                      Kopier link
                    </a>
                    <a href={getEmailLink()} target="_blank" rel="noreferrer">
                      Del via email
                    </a>
                    <a href={getSmsLink()} target="_blank" rel="noreferrer">
                      Del via SMS
                    </a>
                  </li>
                </ul>
              </div>
              {isValid ? (
                <>
                  <MediaSequenceCreate
                    existingMediaSequence={data}
                    trigger={
                      <button className="btn btn-primary">
                        Lav kopi af video
                      </button>
                    }
                  />
                  <MutateEntity
                    trigger={
                      <button type="button" className="btn btn-warning">
                        Lav video om
                      </button>
                    }
                    title={`Lav videoen ${mediaSequence.name} om?`}
                    description={`Er du sikker på du vil lave videoen '${mediaSequence.name}' om? Din opsætning vil blive gemt men resultaterne vil blive slettet.`}
                    setter={useRedoMediaSequence}
                    mutationVariables={{ id: mediaSequence.id }}
                  />
                </>
              ) : null}
            </div>
            <div id="results-overview">
              <MediaSequenceBranchResultTable
                data={data}
                isFetching={false}
                disabled={disabled}
              />
            </div>
          </>
        ) : null}
      </div>

      <div className="flex flex-col mt-4 mb-8">
        <div id="step-navigation" className="join justify-center mt-2">
          <button className="join-item btn" onClick={handlePrevious}>
            Forrige trin
          </button>
          <PermissionProtectedComponent
            permissions={[EPermission.ModuleSocialMedia]}
          >
            <button
              data-testid="media-sequence-next-step"
              className="join-item btn"
              onClick={handleNext}
              disabled={disableNext}
            >
              Næste trin
            </button>
          </PermissionProtectedComponent>
        </div>
        <PermissionProtectedComponent
          permissions={[EPermission.ModuleSocialMedia]}
        >
          {disableNext && disableText ? (
            <div className="text-center mt-2">
              <p className="text-sm text-gray-500">{disableText}</p>
            </div>
          ) : null}
        </PermissionProtectedComponent>
      </div>
    </>
  );
};
